import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import MacroProcessEditModal from '@/features/macro-process-edit/components/MacroProcessEditModal'
import { Spinner } from '@/components/ui'
import Pagination from '@/components/ui/Pagination'
import {
  fetchMacroProcesses,
  fetchTasksForContainers,
  setFilters,
  archiveMacroProcess,
  deleteMacroProcess,
  validateProcessTask,
  reopenProcessTask
} from './macroProcessesSlice'
import { completeProcessTask } from './processTasksSlice'
import MacroProcessSection from './components/MacroProcessSection'

/**
 * Page dédiée à la gestion des Process
 * Affiche les MacroProcesses regroupés par bien, avec hiérarchie MacroProcess → ProcessContainer → ProcessTask
 */
const ProcessTasksPage = () => {
  const dispatch = useDispatch()

  const macroProcesses = useSelector(state => state.macroProcesses?.items || [])
  const containerIds = useSelector(
    state => (state.macroProcesses?.items || []).flatMap(mp =>
      (mp.process_containers || []).map(c => c.id)
    ),
    shallowEqual
  )
  const tasks = useSelector(state => state.macroProcesses?.tasks || {})
  const loading = useSelector(state => state.macroProcesses?.loading?.fetchList || false)
  const loadingTasks = useSelector(state => state.macroProcesses?.loading?.fetchTasks || false)
  const pagination = useSelector(state => state.macroProcesses?.pagination || {})
  const filters = useSelector(state => state.macroProcesses?.filters || {})
  const editSuccess = useSelector(state => state.macroProcessEdit?.requestStatus?.success)
  const currentMacroProcessEdited = useSelector(state => state.macroProcessEdit?.activeMacroProcess)

  // Charger les macro processes au montage et quand les filtres changent
  useEffect(() => {
    dispatch(fetchMacroProcesses({ page: filters.page, status: filters.status, bien_id: filters.bien_id || undefined }))
  }, [dispatch, filters.page, filters.status, filters.bien_id])

  // Rafraîchir les macro processes après un ajout de containers réussi
  useEffect(() => {
    if (editSuccess) {
      dispatch(fetchMacroProcesses({ page: filters.page, status: filters.status, bien_id: filters.bien_id || undefined }))
    }
  }, [editSuccess, dispatch])

  // Charger les tasks pour tous les containers de la page courante
  // Dépend uniquement des IDs de containers (via shallowEqual) pour éviter un rechargement
  // inutile lors des mises à jour de propriétés des macroProcesses ou containers
  useEffect(() => {
    if (containerIds.length > 0) {
      dispatch(fetchTasksForContainers(containerIds))
    }
  }, [containerIds, dispatch])

  const handlePageChange = (page) => {
    dispatch(setFilters({ page }))
  }

  const handleComplete = (task) => dispatch(completeProcessTask(task.id))
  // const handleStart = (task) => dispatch(startProcessTask(task.id))
  const handleValidate = (taskId) => dispatch(validateProcessTask(taskId))
  const handleReopen = (taskId) => dispatch(reopenProcessTask(taskId))
  const handleArchive = (macroProcessId) => dispatch(archiveMacroProcess(macroProcessId))
  const handleDelete = (macroProcessId) => dispatch(deleteMacroProcess(macroProcessId))

  const isLoading = loading || loadingTasks

  // Tri des macroprocessus par codage_interne du bien
  const sortedMacroProcesses = useMemo(() => {
    return [...macroProcesses].sort((a, b) => {
      const codageA = a.bien?.codage_interne || ''
      const codageB = b.bien?.codage_interne || ''
      return codageA.localeCompare(codageB)
    })
  }, [macroProcesses])

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold">Process</h1>
      </div>

      {/* État de chargement */}
      {isLoading && (
        <div className="flex justify-center p-8">
          <Spinner color="primary" size="lg" />
        </div>
      )}
      
      {/* État vide */}
      {!isLoading && macroProcesses.length === 0 && (
        <div className="text-center p-8 bg-gray-100 rounded-lg">
          <p className="text-gray-600 text-lg">Aucun processus en cours</p>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Liste des MacroProcesses */}
      {!isLoading && macroProcesses.length > 0 && (
        <div className="space-y-8">
          {sortedMacroProcesses.map(macroProcess => (
            <MacroProcessSection
              key={macroProcess.id}
              macroProcess={macroProcess}
              tasks={tasks}
              expanded={currentMacroProcessEdited && macroProcess.id == currentMacroProcessEdited.id}
              onComplete={handleComplete}
              onValidate={handleValidate}
              onReopen={handleReopen}
              onCancel={handleArchive}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Modal d'édition partagé — une seule instance dans la page */}
      <MacroProcessEditModal />
    </div>
  )
}

export default ProcessTasksPage
